/**
 * Utilidades puras de la cola de expedientes: urgencia y composición por tipo de la card, y
 * señales de llamada entrante para la campana. Sin React ni store, para poder testearlas.
 */
import { ExpedientQueueItem } from './expedientQueueTypes';

/**
 * Item sintético de «Mis expedientes»: mis working alerts de un expediente con el mismo shape
 * que la cola v2 (la card y la atención no distinguen el origen).
 */
export const myExpedientItem = (expedientId: string, alarms: any[]): ExpedientQueueItem => ({
  expedientId,
  clientId: alarms[0]?.client?.uuid ?? null,
  expedientClass: 'ATTENTION',
  maxPriority: 0,
  oldestTmst: Math.min(...alarms.map((alarm) => Number(alarm?.alertTimestamp) || Date.now())),
  pendingAlerts: alarms.length,
  totalAliveAlerts: alarms.length,
  newSinceLastAttention: 0,
  lastOperatorId: null,
  affinityOperatorId: null,
  affinityUntilTmst: null,
  alerts: alarms,
});

/** Tipos que son una persona al teléfono esperando a que alguien descuelgue. */
export const INCOMING_CALL_TYPES = ['A', 'Callback', 'CallbackIp', 'CallbackVideo'];

/**
 * Peticiones de llamada (Llámame, Llámame (Técnica), pulsación y solicitud): el usuario pide que le
 * llamen, pero no hay nadie al teléfono. Se atienden como cualquier alerta (no se «contestan» y se
 * adoptan solas); solo comparten con las llamadas el badge del teléfono en la campana.
 */
export const CALL_REQUEST_TYPES = ['CALLME', 'CALLME_TECH', 'CALL', 'CALL_REQUEST'];

export const isIncomingCallType = (type?: string | null): boolean => !!type && INCOMING_CALL_TYPES.includes(type);

export const isCallRequestType = (type?: string | null): boolean => !!type && CALL_REQUEST_TYPES.includes(type);

/** Desempate entre tipos con la misma prioridad numérica del SAC (menor = más urgente). */
const CATEGORY_RANK: Record<string, number> = {
  FALL_DETECTION: 1,
  MeasureOutOfRange: 2,
  ServiceStateChange: 3,
  LostCall: 4,
  Agenda: 5,
  ServiceReview: 6,
  TechIncident: 7,
};

export const categoryRank = (type?: string | null): number =>
  isIncomingCallType(type) ? 0 : CATEGORY_RANK[type ?? ''] ?? 9;

/** PRIORITY del SAC: VARCHAR numérico, MAYOR = más urgente (la cola ordena por MAX desc). */
export const alertPriority = (alert: any): number => {
  const n = Number(alert?.priority);
  return Number.isFinite(n) ? n : 0;
};

/** Sin estado (cards de «Mis expedientes» o SAC antiguo) se considera pendiente. */
export const isPendingAlert = (alert: any): boolean => !alert?.state || alert.state === 'PENDING_HANDLE';

const alertTmst = (alert: any): number => Number(alert?.alertTimestamp ?? alert?.receivedTmst ?? 0);

/** Orden de urgencia: prioridad del SAC desc, categoría, y la más antigua primero. */
export const compareUrgency = (a: any, b: any): number =>
  alertPriority(b) - alertPriority(a) || categoryRank(a?.type) - categoryRank(b?.type) || alertTmst(a) - alertTmst(b);

export const mostUrgentAlert = (alerts: any[] | undefined | null): any | null => {
  const list = (alerts ?? []).filter(Boolean);
  if (list.length === 0) return null;
  return [...list].sort(compareUrgency)[0];
};

export interface TypeCount {
  type: string;
  count: number;
  incomingCall: boolean;
}

/**
 * Composición de la card: una entrada por tipo, ordenadas por urgencia. Cuenta solo las
 * PENDIENTES (lo que el operador va a coger) salvo que se pida todo («Mis expedientes»:
 * sus alertas ya están en atención).
 */
export const countAlertsByType = (alerts: any[] | undefined | null, { includeAll = false } = {}): TypeCount[] => {
  const considered = (alerts ?? []).filter((alert) => alert?.type && (includeAll || isPendingAlert(alert)));
  const byType = new Map<string, any[]>();
  considered.forEach((alert) => {
    const list = byType.get(alert.type) ?? [];
    list.push(alert);
    byType.set(alert.type, list);
  });
  return Array.from(byType.entries())
    .map(([type, list]) => ({ type, count: list.length, representative: [...list].sort(compareUrgency)[0] }))
    .sort((a, b) => compareUrgency(a.representative, b.representative))
    .map(({ type, count }) => ({ type, count, incomingCall: isIncomingCallType(type) }));
};

export const alertKey = (alert: any): string | null => {
  const id = alert?.uuid ?? alert?.id;
  return id ? String(id) : null;
};

export interface PendingSplit {
  /** Llamadas entrantes pendientes: alguien al teléfono, solo se contestan con un clic. */
  calls: any[];
  /** El resto de pendientes: se adoptan solas. */
  others: any[];
}

/**
 * Pendientes de un expediente que ya tengo en mano (exclusividad: el SAC solo se las enseña al
 * titular). Las llamadas entrantes esperan al clic del operador —adoptarlas sola contestaría sin
 * que nadie pulsara—; las demás se adoptan automáticamente.
 */
export const splitPendingAlerts = (expedient: any): PendingSplit => {
  const pending = (expedient?.alerts ?? []).filter(isPendingAlert);
  return {
    calls: pending.filter((alert: any) => isIncomingCallType(alert?.type)),
    others: pending.filter((alert: any) => !isIncomingCallType(alert?.type)),
  };
};

/** El item trae alguna alerta en atención (PROCESSING): el propio SAC dice que está en mano. */
export const hasAlertsInAttention = (expedient: any): boolean =>
  (expedient?.alerts ?? []).some((alert: any) => alert?.state === 'PROCESSING');

/**
 * Expediente al que se ata una saliente (llamadas-desde-expediente): el que pide quien llama (ficha
 * del expediente) o, si no, el que estoy atendiendo de ese mismo usuario. Sin esto la llamada cae en
 * el colector del usuario, que no tiene por qué ser el atendido (una incidencia técnica es un
 * expediente propio). Solo si la selección sigue en mi mano: una selección rancia reabriría en el
 * SAC un expediente ya cerrado.
 */
export const expedientIdForCall = (
  requestedExpedientId: string | null | undefined,
  userId: string | null | undefined,
  attended: any,
): string | null => {
  if (requestedExpedientId) return requestedExpedientId;
  if (!userId || !attended?.expedientId || attended.clientId !== userId) return null;
  return hasAlertsInAttention(attended) ? attended.expedientId : null;
};

/**
 * Alertas nuevas de un expediente mío que se adoptan solas (las que no son llamada). Exige que
 * el MISMO item de la cola traiga alguna alerta en atención: tras aparcar, el refresco de la
 * cola llega antes que el de mis working alerts y, mirando solo estas, lo recién aparcado
 * seguía pareciendo mío y se readoptaba (PRE 16/10: aparcar costaba dos clics). Con el SAC de
 * exclusividad, un item de MI cola con alertas PROCESSING solo puede estar en mi mano.
 */
export const autoAdoptableAlertIds = (expedient: any): string[] =>
  hasAlertsInAttention(expedient)
    ? splitPendingAlerts(expedient)
        .others.map(alertKey)
        .filter((id): id is string => !!id)
    : [];

export const hasWaitingIncomingCall = (expedient: any): boolean =>
  (expedient?.alerts ?? []).some((alert: any) => isPendingAlert(alert) && isIncomingCallType(alert?.type));

/** Llamada entrante o petición de llamada esperando: lo que enciende el teléfono de la campana. */
export const hasWaitingPhoneAlert = (expedient: any): boolean =>
  (expedient?.alerts ?? []).some(
    (alert: any) => isPendingAlert(alert) && (isIncomingCallType(alert?.type) || isCallRequestType(alert?.type)),
  );

/** Expedientes de la cola con una llamada entrante o una petición de llamada esperando (badge de la campana). */
export const countWaitingIncomingCalls = (expedients: any[] | undefined | null): number =>
  (expedients ?? []).filter(hasWaitingPhoneAlert).length;

/** Si el consume de una alerta adoptada falla, se vuelve a intentar pasado este margen. */
export const ADOPT_RETRY_MS = 60_000;

const adoptionAttempts = new Map<string, number>();

/**
 * Reserva alertas para adoptarlas una sola vez (reintento pasado ADOPT_RETRY_MS). Registro único
 * para la campana (página de la cola) y el panel de atención (estado vivo del expediente): miran
 * fuentes distintas y podrían pedir el mismo consume a la vez.
 */
export const claimForAdoption = (alertIds: string[], now = Date.now()): string[] => {
  const claimed = alertIds.filter((id) => {
    const last = adoptionAttempts.get(id);
    return last === undefined || last < now - ADOPT_RETRY_MS;
  });
  claimed.forEach((id) => adoptionAttempts.set(id, now));
  return claimed;
};

/** Solo para tests. */
export const resetAdoptionClaims = () => adoptionAttempts.clear();

/**
 * Alerta mínima a partir de una entrada del estado vivo del SAC (sin objeto completo en la
 * página de la cola ni en las working alerts). Sin `alertId` (sala de Jitsi): la ventana de
 * llamada espera a la versión completa de las working alerts antes de entrar en ninguna sala.
 */
export const liveAlertToAlert = (live: any, expedientId: string | null, client: any = null) => ({
  uuid: live?.alertId,
  type: live?.type,
  state: live?.state,
  priority: live?.priority,
  alertTimestamp: live?.createdTs,
  expedient: { id: expedientId },
  client,
});
